import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '../../../../../constants/axiosInstance';
import type {
  CreateEscalationPayload,
  EscalationFormData,
  EscalationFormErrors,
  EscalationGroup,
  UpdateEscalationPayload,
  User,
} from '../../../types';

export const fetchUsersForEscalation = async (searchString: string): Promise<User[]> => {
  const response = await axiosInstance().post('/newUsers/getUserGroupsForUser', {userGroup: searchString});
  return response.data.data || [];
};

const createEscalationApi = async (payload: CreateEscalationPayload) => {
  const response = await axiosInstance().post('/escalation/createAndUpdate', payload);
  return response?.data;
};

const initialFormData: EscalationFormData = {
  userGroup: null,
  plants: [],
  modules: [],
  attendanceManager: [],
  rosterManager: [],
  operator: [],
  escalationGroups: [],
};

const createDefaultEscalationGroup = (): EscalationGroup => ({
  id: uuidv4(),
  users: [],
  value: '',
  unit: 'Hour(s)',
});

interface UseEscalationFormOptions {
  initialData?: EscalationFormData;
  isEditMode?: boolean;
}

export const useEscalationForm = ({
  initialData,
  isEditMode = false,
}: UseEscalationFormOptions) => {
  const params = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const escalationId = params?.escalationId as string;

  const [formData, setFormData] = useState<EscalationFormData>(initialData || initialFormData);
  const [errors, setErrors] = useState<EscalationFormErrors>({});

  const createMutation = useMutation<any, Error, CreateEscalationPayload>({
    mutationFn: createEscalationApi,
    onSuccess: response => {
      toast.success(response?.message || 'Escalation created successfully');
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
      navigate('/escalation');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create escalation');
    },
  });

  const updateMutation = useMutation<any, Error, UpdateEscalationPayload>({
    mutationFn: createEscalationApi,
    onSuccess: response => {
      toast.success(response?.message || 'Escalation updated successfully');
      queryClient.invalidateQueries({ queryKey: ['escalations'] });
      queryClient.invalidateQueries({ queryKey: ['escalation', escalationId] });
      navigate('/escalation');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update escalation');
    },
  });

  const validateForm = useCallback((): boolean => {
    const newErrors: EscalationFormErrors = {};

    if (!formData.userGroup) {
      newErrors.userGroup = 'User group is required';
    }
    if (formData.plants.length === 0) {
      newErrors.plants = 'At least one plant must be selected';
    }

    if (formData.modules.length === 0) {
      newErrors.modules = 'At least one module must be selected';
    }

    const isAttendanceSelected = formData.modules.includes('Attendance');
    const isTaskEscalationSelected = formData.modules.includes('Task Escalation');

    if (isAttendanceSelected) {
      if (formData.attendanceManager.length === 0) {
        newErrors.attendanceManager = 'At least one attendance manager must be selected';
      }
      if (formData.rosterManager.length === 0) {
        newErrors.rosterManager = 'At least one roster manager must be selected';
      }
      if (formData.operator.length === 0) {
        newErrors.operator = 'At least one operator must be selected';
      }
    }

    const escalationGroupErrors: { [key: string]: { users?: string; time?: string } } = {};
    let hasGroupErrors = false;

    if (isTaskEscalationSelected) {
      if (formData.escalationGroups.length === 0) {
        newErrors.escalationGroups = 'At least one escalation group is required';
      } else {
        formData.escalationGroups.forEach(group => {
          const groupErrors: { users?: string; time?: string } = {};
          let currentGroupHasError = false;

          if (group.users.length === 0) {
            groupErrors.users = 'At least one user must be selected';
            currentGroupHasError = true;
          }
          if (!group.value || isNaN(Number(group.value)) || Number(group.value) <= 0) {
            groupErrors.time = 'Valid time value is required';
            currentGroupHasError = true;
          }

          if (currentGroupHasError) {
            escalationGroupErrors[group.id] = groupErrors;
            hasGroupErrors = true;
          }
        });

        if (hasGroupErrors) {
          newErrors.escalationGroups = escalationGroupErrors;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const onFormChange = useCallback(
    <K extends keyof EscalationFormData>(field: K, value: EscalationFormData[K]) => {
      setFormData(prev => {
        const newState = { ...prev, [field]: value };

        if (field === 'userGroup') {
          newState.plants = [];
          newState.modules = [];
          newState.attendanceManager = [];
          newState.rosterManager = [];
          newState.operator = [];
          newState.escalationGroups = [];
        } else if (field === 'modules') {
          const prevModules = prev.modules;
          const newModules = value as string[];

          const wasAttendanceSelected = prevModules.includes('Attendance');
          const isAttendanceNowSelected = newModules.includes('Attendance');
          const wasTaskEscalationSelected = prevModules.includes('Task Escalation');
          const isTaskEscalationNowSelected = newModules.includes('Task Escalation');

          if (wasAttendanceSelected && !isAttendanceNowSelected) {
            newState.attendanceManager = [];
            newState.rosterManager = [];
            newState.operator = [];
          }
          if (wasTaskEscalationSelected && !isTaskEscalationNowSelected) {
            newState.escalationGroups = [];
          } else if (!wasTaskEscalationSelected && isTaskEscalationNowSelected) {
            if (newState.escalationGroups.length === 0) {
              newState.escalationGroups = [createDefaultEscalationGroup()];
            }
          }
        }
        return newState;
      });

      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[field];

        if (field === 'userGroup') {
          delete newErrors.plants;
          delete newErrors.modules;
          delete newErrors.attendanceManager;
          delete newErrors.rosterManager;
          delete newErrors.operator;
          delete newErrors.escalationGroups; 
        } else if (field === 'modules') {
          const newModules = value as string[];
          if (!newModules.includes('Attendance')) {
            delete newErrors.attendanceManager;
            delete newErrors.rosterManager;
            delete newErrors.operator;
          }
          if (!newModules.includes('Task Escalation')) {
            delete newErrors.escalationGroups;
          }
        }
        return newErrors;
      });
    },
    []
  );

  const handleEscalationGroupUpdate = useCallback(
    (groupId: string, updates: Partial<EscalationGroup>) => {
      setFormData(prevData => ({
        ...prevData,
        escalationGroups: prevData.escalationGroups.map(group =>
          group.id === groupId ? { ...group, ...updates } : group
        ),
      }));

      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        if (typeof newErrors.escalationGroups === 'object' && newErrors.escalationGroups !== null) {
          const groupSpecificErrors = { ...newErrors.escalationGroups[groupId] };
          let errorsCleared = false;

          if ('users' in updates) {
            delete groupSpecificErrors.users;
            errorsCleared = true;
          }
          if ('value' in updates || 'unit' in updates) {
            delete groupSpecificErrors.time;
            errorsCleared = true;
          }

          if (errorsCleared) {
            if (Object.keys(groupSpecificErrors).length === 0) {
              delete newErrors.escalationGroups[groupId];
            } else {
              newErrors.escalationGroups[groupId] = groupSpecificErrors;
            }

            if (
              typeof newErrors.escalationGroups === 'string' &&
              formData.escalationGroups.length > 0
            ) {
              delete newErrors.escalationGroups;
            }
          }
        }
        return newErrors;
      });
    },
    [formData.escalationGroups]
  );

  const addEscalationGroup = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      escalationGroups: [...prev.escalationGroups, createDefaultEscalationGroup()],
    }));
    setErrors(prev => {
      const newErrors = { ...prev };
      if (typeof newErrors.escalationGroups === 'string') {
        delete newErrors.escalationGroups;
      }
      return newErrors;
    });
  }, []);

  const removeEscalationGroup = useCallback(
    (groupId: string) => {
      setFormData(prev => ({
        ...prev,
        escalationGroups: prev.escalationGroups.filter(group => group.id !== groupId),
      }));
      setErrors(prev => {
        const newErrors = { ...prev };
        if (
          newErrors.escalationGroups &&
          typeof newErrors.escalationGroups === 'object' &&
          newErrors.escalationGroups !== null
        ) {
          delete newErrors.escalationGroups[groupId];
          if (
            Object.keys(newErrors.escalationGroups).length === 0 &&
            formData.escalationGroups.length - 1 === 0
          ) {
            if (formData.modules.includes('Task Escalation')) {
              newErrors.escalationGroups = 'At least one escalation group is required';
            } else {
              delete newErrors.escalationGroups;
            }
          } else if (Object.keys(newErrors.escalationGroups).length === 0) {
            delete newErrors.escalationGroups;
          }
        }
        return newErrors;
      });
    },
    [formData.escalationGroups, formData.modules]
  );

  const handleSubmit = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    const payload: CreateEscalationPayload = {
      userGroup: formData?.userGroup?._id!,
      plants: formData.plants.map(plant => plant._id),
      modules: formData.modules
    };

    const isAttendanceSelected = formData.modules.includes('Attendance');
    const isTaskEscalationSelected = formData.modules.includes('Task Escalation');

    if (isAttendanceSelected) {
      payload.attendanceManager = formData.attendanceManager.map(manager => manager._id);
      payload.rosterManager = formData.rosterManager.map(manager => manager._id);
      payload.operator = formData.operator.map(manager => manager._id);
    }else{
      payload.attendanceManager = [];
      payload.rosterManager = [];
      payload.operator = [];
    }

    if (isTaskEscalationSelected) {
      payload.escalations = formData.escalationGroups.map(group => ({
        value: Number(group.value),
        unit: group.unit,
        userIds: group.users.map(user => user._id),
      }));
    }else{
      payload.escalations = [];
    }

    if (isEditMode) {
      updateMutation.mutate({ ...payload, escalationId });
    } else {
      createMutation.mutate(payload);
    }
  }, [formData, validateForm, isEditMode, escalationId, createMutation, updateMutation]);

  const handleCancel = useCallback(() => {
    navigate('/escalation');
  }, [navigate]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
  }, []);

  const isLoading = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  );

  const isPageLoading = useMemo(() => isEditMode && !initialData, [isEditMode, initialData]);

  const pageTitle = useMemo(
    () => (isEditMode ? 'Edit Escalation' : 'Create New Escalation'),
    [isEditMode]
  );

  return {
    formData,
    errors,
    isEditMode,
    pageTitle,
    isLoading,
    isPageLoading,
    onFormChange,
    onEscalationGroupUpdate: handleEscalationGroupUpdate,
    addEscalationGroup,
    removeEscalationGroup,
    handleSubmit,
    handleCancel,
    resetForm,
    validateForm,
  };
};